import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import '../styles/Groups.css';

const API_URL = process.env.REACT_APP_API_URL;

const CATEGORY_TONE = { Ders: 'ders', Proje: 'proje', 'Sınav': 'sinav', Genel: 'genel' };
const CATEGORIES = ['Tümü', 'Ders', 'Proje', 'Sınav', 'Genel'];

const SCOPES = [
  { value: 'all', label: 'Tüm gruplar' },
  { value: 'mine', label: 'Gruplarım' },
];
const SORTS = [
  { value: 'default', label: 'Varsayılan sıralama' },
  { value: 'members', label: 'En çok üyesi olan' },
  { value: 'name', label: 'Ada göre (A-Z)' },
];

const JOIN_TYPES = [
  { value: 'approval', label: 'Başvuru ile (yönetici onayı)', hint: 'Katılmak isteyenler başvuru gönderir, siz onaylarsınız.' },
  { value: 'code', label: 'Davet kodu ile', hint: 'Yalnızca davet kodunu bilenler katılabilir.' },
  { value: 'open', label: 'Herkese açık', hint: 'Herkes tek tıkla katılabilir.' },
];
const JOIN_LABELS = { open: 'Herkese açık', approval: 'Onaylı', code: 'Kodlu' };

const MAX_NAME_LENGTH = 20;
const MAX_DESCRIPTION_LENGTH = 50;
const MAX_SCHOOL_LENGTH = 50;

const SearchIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Kimlik doğrulamalı istek: hata durumunda sunucunun mesajını Error olarak fırlatır
const authFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Oturum bulunamadı. Lütfen giriş yapınız.');

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }

  if (!res.ok) {
    const error = new Error((data && (data.message || data.error)) || `Hata: ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return data;
};

const fullName = (person) =>
  `${person?.first_name || ''} ${person?.last_name || ''}`.trim() || 'İsimsiz kullanıcı';

const lower = (text) => (text || '').toLocaleLowerCase('tr-TR');

const isMemberOf = (group, user) =>
  !!user && !!(group.is_member ?? (Array.isArray(group.members) && group.members.includes(user.userId)));
const isAdminOf = (group, user) => !!user && String(group.admin_id) === String(user.userId);
const memberCountOf = (group) => group.member_count ?? (group.members ? group.members.length : 0);

/* ---------- Ortak pencere ---------- */
// Açık pencerelerin sırası: Escape yalnızca en üstteki pencereyi kapatır
const modalStack = [];

const Modal = ({ title, onClose, size, children }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const token = {};
    modalStack.push(token);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape' && modalStack[modalStack.length - 1] === token) {
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      const index = modalStack.indexOf(token);
      if (index !== -1) modalStack.splice(index, 1);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div className="gr-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className={`gr-modal${size === 'sm' ? ' gr-modal-sm' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="gr-modal-head">
          <h2>{title}</h2>
          <button type="button" className="gr-modal-close" onClick={onClose} aria-label="Kapat">×</button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ---------- Onay penceresi ---------- */
const ConfirmDialog = ({ title, message, confirmLabel, onConfirm, onCancel }) => {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    // Pencereyi kapatma işini üst bileşen yapar; burada state geri alınmaz
    await onConfirm();
  };

  return (
    <Modal title={title} size="sm" onClose={busy ? () => {} : onCancel}>
      <div className="gr-modal-body">
        <p className="gr-modal-text">{message}</p>
        <div className="gr-modal-actions">
          <button type="button" className="gr-btn gr-btn-outline" onClick={onCancel} disabled={busy}>
            Vazgeç
          </button>
          <button type="button" className="gr-btn gr-btn-danger" onClick={handleConfirm} disabled={busy} autoFocus>
            {busy ? 'İşleniyor...' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ---------- Yeni grup oluşturma penceresi ---------- */
const CreateGroupDialog = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Ders');
  const [school, setSchool] = useState('');
  const [joinType, setJoinType] = useState('approval');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const selectedJoinType = JOIN_TYPES.find((t) => t.value === joinType);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!name.trim() || !description.trim()) {
      setError('Grup adı ve açıklama boş olamaz.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim(),
        category,
        school: school.trim() || null,
        joinType,
      });
    } catch (err) {
      setError(`Grup oluşturulamadı: ${err.message}`);
      setBusy(false);
    }
  };

  return (
    <Modal title="Yeni grup oluştur" onClose={busy ? () => {} : onClose}>
      <form className="gr-modal-body" onSubmit={submit} noValidate>
        <label className="gr-field">
          <span className="gr-label">
            Grup adı <small>{name.length}/{MAX_NAME_LENGTH}</small>
          </span>
          <input
            className="gr-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Örn. Veri Yapıları Çalışma"
            maxLength={MAX_NAME_LENGTH}
            autoFocus
          />
        </label>

        <label className="gr-field">
          <span className="gr-label">
            Açıklama <small>{description.length}/{MAX_DESCRIPTION_LENGTH}</small>
          </span>
          <textarea
            className="gr-input gr-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Grubun amacını kısaca yazın"
            rows={2}
            maxLength={MAX_DESCRIPTION_LENGTH}
          />
        </label>

        <div className="gr-form-row">
          <label className="gr-field">
            <span className="gr-label">Kategori</span>
            <select className="gr-input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.filter((c) => c !== 'Tümü').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="gr-field">
            <span className="gr-label">Okul adı <small>isteğe bağlı</small></span>
            <input
              className="gr-input"
              type="text"
              value={school}
              onChange={(e) => setSchool(e.target.value)}
              maxLength={MAX_SCHOOL_LENGTH}
            />
          </label>
        </div>

        <fieldset className="gr-field gr-fieldset">
          <legend className="gr-label">Kimler katılabilir?</legend>
          <div className="gr-radio-list">
            {JOIN_TYPES.map((t) => (
              <label key={t.value} className={`gr-radio${joinType === t.value ? ' is-active' : ''}`}>
                <input
                  type="radio"
                  name="create-join-type"
                  value={t.value}
                  checked={joinType === t.value}
                  onChange={() => setJoinType(t.value)}
                />
                <span><b>{t.label}</b><small>{t.hint}</small></span>
              </label>
            ))}
          </div>
          <span className="gr-sr-only">{selectedJoinType?.hint}</span>
        </fieldset>

        {error && <p className="gr-form-error" role="alert">{error}</p>}

        <div className="gr-modal-actions">
          <button type="button" className="gr-btn gr-btn-outline" onClick={onClose} disabled={busy}>İptal</button>
          <button type="submit" className="gr-btn gr-btn-solid" disabled={busy}>
            {busy ? 'Oluşturuluyor...' : 'Oluştur'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------- Davet koduyla katılma ---------- */
const JoinCodeDialog = ({ group, onClose, onSubmit }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await onSubmit(group.id, code.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Davet kodu ile katıl" onClose={onClose}>
      <form className="gr-modal-body" onSubmit={submit}>
        <p className="gr-modal-text">
          <b>{group.name}</b> grubuna katılmak için yöneticiden aldığınız davet kodunu girin.
        </p>
        <label className="gr-field">
          <span className="gr-label">Davet kodu</span>
          <input
            className="gr-input gr-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={16}
            autoFocus
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
          />
        </label>
        <div className="gr-modal-actions">
          <button type="button" className="gr-btn gr-btn-outline" onClick={onClose}>İptal</button>
          <button type="submit" className="gr-btn gr-btn-solid" disabled={busy || !code.trim()}>
            {busy ? 'Katılınıyor...' : 'Katıl'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------- Yönetici paneli ---------- */
const ManageDialog = ({ group, onClose, onChanged, askConfirm }) => {
  const { showNotification } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await authFetch(`/api/groups/${group.id}/manage`));
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [group.id, showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      if (successMessage) showNotification(successMessage, 'success');
      await load();
      onChanged();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const changeJoinType = (joinType) =>
    run(
      () => authFetch(`/api/groups/${group.id}/settings`, { method: 'PUT', body: JSON.stringify({ joinType }) }),
      'Katılım türü güncellendi.'
    );

  const regenerateCode = () =>
    askConfirm({
      title: 'Davet kodunu yenile',
      message: 'Yeni bir davet kodu oluşturulacak ve eski kod artık çalışmayacak. Devam edilsin mi?',
      confirmLabel: 'Yenile',
      onConfirm: () =>
        run(
          () => authFetch(`/api/groups/${group.id}/settings`, { method: 'PUT', body: JSON.stringify({ regenerateCode: true }) }),
          'Yeni davet kodu oluşturuldu. Eski kod artık çalışmaz.'
        ),
    });

  const decide = (requestId, action) =>
    run(
      () => authFetch(`/api/groups/${group.id}/requests/${requestId}`, { method: 'PUT', body: JSON.stringify({ action }) }),
      action === 'approve' ? 'Başvuru onaylandı.' : 'Başvuru reddedildi.'
    );

  const removeMember = (member) =>
    askConfirm({
      title: 'Üyeyi çıkar',
      message: `${fullName(member)} gruptan çıkarılsın mı?`,
      confirmLabel: 'Çıkar',
      onConfirm: () =>
        run(
          () => authFetch(`/api/groups/${group.id}/members/${member.id}`, { method: 'DELETE' }),
          'Üye gruptan çıkarıldı.'
        ),
    });

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.invite_code);
      showNotification('Davet kodu kopyalandı.', 'success');
    } catch (e) {
      showNotification('Kod kopyalanamadı.', 'error');
    }
  };

  return (
    <Modal title={`Yönet: ${group.name}`} onClose={onClose}>
      {loading ? (
        <p className="gr-modal-text">Yükleniyor...</p>
      ) : !data ? (
        <p className="gr-modal-text">Yönetim bilgileri alınamadı.</p>
      ) : (
        <div className="gr-modal-body">
          {data.requests.length > 0 && (
            <section className="gr-section gr-section-alert">
              <h3>Bekleyen başvurular <span className="gr-pill is-hot">{data.requests.length}</span></h3>
              <ul className="gr-people">
                {data.requests.map((r) => (
                  <li key={r.id}>
                    <span className="gr-person-name">{fullName(r)}</span>
                    <span className="gr-person-actions">
                      <button type="button" className="gr-btn gr-btn-solid gr-btn-sm" disabled={busy} onClick={() => decide(r.id, 'approve')}>Onayla</button>
                      <button type="button" className="gr-btn gr-btn-danger-outline gr-btn-sm" disabled={busy} onClick={() => decide(r.id, 'reject')}>Reddet</button>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="gr-section">
            <h3>Katılım türü</h3>
            <div className="gr-radio-list">
              {JOIN_TYPES.map((t) => (
                <label key={t.value} className={`gr-radio${data.join_type === t.value ? ' is-active' : ''}`}>
                  <input
                    type="radio"
                    name="join-type"
                    value={t.value}
                    checked={data.join_type === t.value}
                    disabled={busy}
                    onChange={() => changeJoinType(t.value)}
                  />
                  <span><b>{t.label}</b><small>{t.hint}</small></span>
                </label>
              ))}
            </div>

            {data.join_type === 'code' && (
              <div className="gr-code-box">
                <span className="gr-label">Davet kodu</span>
                <div className="gr-code-row">
                  <code className="gr-code">{data.invite_code}</code>
                  <button type="button" className="gr-btn gr-btn-outline gr-btn-sm" onClick={copyCode}>Kopyala</button>
                  <button type="button" className="gr-btn gr-btn-outline gr-btn-sm" onClick={regenerateCode} disabled={busy}>Yenile</button>
                </div>
              </div>
            )}
          </section>

          {data.requests.length === 0 && (
            <section className="gr-section">
              <h3>Bekleyen başvurular <span className="gr-pill">0</span></h3>
              <p className="gr-muted">Bekleyen başvuru yok.</p>
            </section>
          )}

          <section className="gr-section">
            <h3>Üyeler <span className="gr-pill">{data.members.length}</span></h3>
            <ul className="gr-people">
              {data.members.map((m) => {
                const isAdminMember = String(m.id) === String(data.admin_id);
                return (
                  <li key={m.id}>
                    <span className="gr-person-name">
                      {fullName(m)} {isAdminMember && <span className="gr-admin">Yönetici</span>}
                    </span>
                    {!isAdminMember && (
                      <button type="button" className="gr-btn gr-btn-danger-outline gr-btn-sm" disabled={busy} onClick={() => removeMember(m)}>
                        Çıkar
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      )}
    </Modal>
  );
};

/* ---------- Grup kartları ---------- */
const GroupList = ({ groups, user, handlers, query }) => {
  const { showNotification } = useNotification();
  const { onDelete, onJoin, onLeave, onRequest, onCancelRequest, onOpenCode, onManage, askConfirm } = handlers;

  const handleNonMemberClick = (groupName) => {
    showNotification(`Bu gruba henüz katılmadınız: ${groupName}`, 'error');
  };

  return (
    <ul className="gr-list">
      {groups.map((group, index) => {
        const isAdmin = isAdminOf(group, user);
        const isMember = isMemberOf(group, user);
        const memberCount = memberCountOf(group);
        const joinType = group.join_type || 'open';
        const pending = group.pending_requests > 0;

        const header = (
          <div className="gr-card-header">
            <strong className="gr-card-title">{group.name}</strong>
            <div className="gr-badges">
              {isMember && isAdmin && <span className="gr-admin">Yönetici</span>}
              {isMember && !isAdmin && <span className="gr-member">Üyesin</span>}
              <span className="gr-tag" data-tone={CATEGORY_TONE[group.category] || 'genel'}>
                {group.category}
              </span>
              <span className="gr-join" data-type={joinType}>{JOIN_LABELS[joinType]}</span>
            </div>
          </div>
        );

        const joinAction = () => {
          if (joinType === 'open') {
            return <button type="button" className="gr-btn gr-btn-solid" onClick={() => onJoin(group.id)}>Gruba katıl</button>;
          }
          if (joinType === 'code') {
            return <button type="button" className="gr-btn gr-btn-solid" onClick={() => onOpenCode(group)}>Kodla katıl</button>;
          }
          if (group.my_request_status === 'pending') {
            return (
              <>
                <button type="button" className="gr-btn gr-btn-outline" disabled>Başvuru bekliyor</button>
                <button type="button" className="gr-link-btn" onClick={() => onCancelRequest(group.id)}>Başvuruyu geri çek</button>
              </>
            );
          }
          return (
            <button type="button" className="gr-btn gr-btn-solid" onClick={() => onRequest(group.id)}>
              {group.my_request_status === 'rejected' ? 'Tekrar başvur' : 'Katılma isteği gönder'}
            </button>
          );
        };

        const body = (
          <>
            {header}
            <p className="gr-card-desc">{group.description}</p>
          </>
        );

        return (
          <li className="gr-card" key={group.id} style={{ '--i': Math.min(index, 8) }}>
            {isMember ? (
              <Link className="gr-card-body" to={`/group/${group.id}`}>{body}</Link>
            ) : (
              <div
                className="gr-card-body gr-card-locked"
                role="button"
                tabIndex={0}
                onClick={() => handleNonMemberClick(group.name)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNonMemberClick(group.name); } }}
              >
                {body}
              </div>
            )}

            <div className="gr-meta">
              <span>Oluşturan: <b>{group.admin_username}</b></span>
              <span>Üye sayısı: <b>{memberCount}</b></span>
              {group.school && group.school.trim() !== '' && (
                <span>Okul: <b>{group.school}</b></span>
              )}
            </div>

            <div className="gr-actions">
              {isMember && (
                <Link to={`/group/${group.id}`} className="gr-btn gr-btn-solid gr-btn-open">Gruba git</Link>
              )}
              {isAdmin ? (
                <>
                  <button type="button" className="gr-btn gr-btn-outline" onClick={() => onManage(group)}>
                    Yönet
                    {pending && <span className="gr-count-badge" aria-label={`${group.pending_requests} bekleyen başvuru`}>{group.pending_requests}</span>}
                  </button>
                  <button
                    type="button"
                    className="gr-btn gr-btn-danger-outline"
                    onClick={() =>
                      askConfirm({
                        title: 'Grubu kapat',
                        message: `"${group.name}" grubunu kapatmak istediğinden emin misin? Bu işlem geri alınamaz.`,
                        confirmLabel: 'Grubu kapat',
                        onConfirm: () => onDelete(group.id),
                      })
                    }
                  >
                    Grubu kapat
                  </button>
                </>
              ) : isMember ? (
                <button
                  type="button"
                  className="gr-btn gr-btn-danger-outline"
                  onClick={() =>
                    askConfirm({
                      title: 'Gruptan ayrıl',
                      message: `"${group.name}" grubundan ayrılmak istediğine emin misin?`,
                      confirmLabel: 'Gruptan ayrıl',
                      onConfirm: () => onLeave(group.id),
                    })
                  }
                >
                  Gruptan ayrıl
                </button>
              ) : (
                joinAction()
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const SkeletonList = () => (
  <ul className="gr-list" aria-hidden="true">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <li className="gr-card gr-skeleton" key={i}>
        <span className="gr-sk gr-sk-title" />
        <span className="gr-sk gr-sk-badges" />
        <span className="gr-sk gr-sk-line" />
        <span className="gr-sk gr-sk-line gr-sk-short" />
        <span className="gr-sk gr-sk-btn" />
      </li>
    ))}
  </ul>
);

// Groups ana bileşeni
function Groups() {
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tümü');
  const [scope, setScope] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [user, setUser] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [codeGroup, setCodeGroup] = useState(null);
  const [manageGroup, setManageGroup] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const { showNotification } = useNotification();

  // Sayfa içi onay: { title, message, confirmLabel, onConfirm }
  const askConfirm = useCallback((options) => setConfirmState(options), []);

  const fetchGroups = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification('Oturum bulunamadı. Lütfen giriş yapınız.', 'error');
      setLoading(false);
      return;
    }
    try {
      const payload = token.split('.')[1];
      setUser(JSON.parse(atob(payload)));
      const data = await authFetch('/api/groups/user');
      setGroups(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (err) {
      console.error('GET Error:', err.message);
      showNotification(err.message, 'error');
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Oluşturma penceresi: hata olursa pencere açık kalır (hata fırlatır)
  const handleCreate = useCallback(
    async (newGroupData) => {
      const newGroup = await authFetch('/api/groups', {
        method: 'POST',
        body: JSON.stringify(newGroupData),
      });
      setShowCreate(false);
      showNotification(
        newGroup?.invite_code
          ? `Grup oluşturuldu! Davet kodu: ${newGroup.invite_code}`
          : 'Grup başarıyla oluşturuldu!',
        'success'
      );
      fetchGroups();
    },
    [showNotification, fetchGroups]
  );

  // Herkese açık gruba katıl / davet koduyla katıl
  const handleJoinGroup = useCallback(
    async (groupId, code) => {
      try {
        await authFetch(`/api/groups/${groupId}/join`, {
          method: 'POST',
          body: JSON.stringify(code ? { code } : {}),
        });
        setCodeGroup(null);
        await fetchGroups();
        showNotification('Gruba başarıyla katıldınız!', 'success');
      } catch (err) {
        console.error('JOIN Error:', err.message);
        showNotification(err.message, 'error');
      }
    },
    [showNotification, fetchGroups]
  );

  const handleRequestJoin = useCallback(
    async (groupId) => {
      try {
        const data = await authFetch(`/api/groups/${groupId}/request`, { method: 'POST' });
        await fetchGroups();
        showNotification(data?.message || 'Başvurunuz gönderildi.', 'success');
      } catch (err) {
        console.error('REQUEST Error:', err.message);
        showNotification(err.message, 'error');
      }
    },
    [showNotification, fetchGroups]
  );

  const handleCancelRequest = useCallback(
    async (groupId) => {
      try {
        await authFetch(`/api/groups/${groupId}/request`, { method: 'DELETE' });
        await fetchGroups();
        showNotification('Başvurunuz geri çekildi.', 'success');
      } catch (err) {
        console.error('CANCEL REQUEST Error:', err.message);
        showNotification(err.message, 'error');
      }
    },
    [showNotification, fetchGroups]
  );

  const handleLeaveGroup = useCallback(
    async (groupId) => {
      try {
        await authFetch(`/api/groups/${groupId}/leave`, { method: 'POST' });
        await fetchGroups();
        showNotification('Gruptan başarıyla ayrıldınız!', 'success');
      } catch (err) {
        console.error('LEAVE Error:', err.message);
        showNotification('Gruptan ayrılırken hata: ' + err.message, 'error');
      }
    },
    [showNotification, fetchGroups]
  );

  const handleDeleteGroup = useCallback(
    async (groupId) => {
      try {
        await authFetch(`/api/groups/${groupId}`, { method: 'DELETE' });
        setGroups((prev) => prev.filter((group) => group.id !== groupId));
        showNotification('Grup başarıyla kapandı!', 'success');
      } catch (err) {
        console.error('DELETE Error:', err.message);
        showNotification('Grup kapatırken hata: ' + err.message, 'error');
      }
    },
    [showNotification]
  );

  // Arama + kapsam uygulanır; kategori sayıları buna göre hesaplanır
  const scopedGroups = useMemo(() => {
    const term = lower(searchTerm.trim());
    return groups.filter((group) => {
      if (scope === 'mine' && !isMemberOf(group, user)) return false;
      if (!term) return true;
      return (
        lower(group.name).includes(term) ||
        lower(group.description).includes(term) ||
        lower(group.school).includes(term)
      );
    });
  }, [groups, searchTerm, scope, user]);

  const categoryCounts = useMemo(() => {
    const counts = { Tümü: scopedGroups.length };
    scopedGroups.forEach((g) => { counts[g.category] = (counts[g.category] || 0) + 1; });
    return counts;
  }, [scopedGroups]);

  const filteredGroups = useMemo(() => {
    const list = scopedGroups.filter((g) => categoryFilter === 'Tümü' || g.category === categoryFilter);
    if (sortBy === 'members') return [...list].sort((a, b) => memberCountOf(b) - memberCountOf(a));
    if (sortBy === 'name') return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
    return list;
  }, [scopedGroups, categoryFilter, sortBy]);

  const myCount = useMemo(() => groups.filter((g) => isMemberOf(g, user)).length, [groups, user]);
  const hasActiveFilters = !!searchTerm.trim() || categoryFilter !== 'Tümü' || scope !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('Tümü');
    setScope('all');
  };

  return (
    <div className="gr-page">
      {/* Başlık */}
      <header className="gr-head">
        <div className="gr-container gr-head-inner">
          <div className="gr-head-copy">
            <h1>Çalışma <span className="gr-mark">gruplarınız.</span></h1>
            <p>Dersler, projeler ve sınavlar için özel gruplar oluşturarak verimli çalışın.</p>
          </div>
          <button
            type="button"
            className="gr-btn gr-btn-solid gr-head-btn"
            onClick={() => setShowCreate(true)}
          >
            <PlusIcon className="gr-btn-icon" />
            Yeni grup oluştur
          </button>
        </div>
      </header>

      <main className="gr-container gr-main">
        {/* Araç çubuğu */}
        <div className="gr-toolbar">
          <label className="gr-search">
            <SearchIcon className="gr-search-icon" />
            <input
              type="search"
              placeholder="Ad, açıklama veya okul ara..."
              aria-label="Grup ara"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="gr-search-input"
            />
          </label>

          <div className="gr-segment" role="group" aria-label="Grup kapsamı">
            {SCOPES.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`gr-segment-btn${scope === s.value ? ' is-active' : ''}`}
                aria-pressed={scope === s.value}
                onClick={() => setScope(s.value)}
              >
                {s.label}
                {s.value === 'mine' && !loading && <span className="gr-segment-count">{myCount}</span>}
              </button>
            ))}
          </div>

          <select
            className="gr-input gr-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sıralama"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div className="gr-filter-row">
          <div className="gr-filters" role="group" aria-label="Kategori filtresi">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={`gr-chip${categoryFilter === c ? ' is-active' : ''}`}
                aria-pressed={categoryFilter === c}
                onClick={() => setCategoryFilter(c)}
              >
                {c}
                {!loading && <span className="gr-chip-count">{categoryCounts[c] || 0}</span>}
              </button>
            ))}
          </div>
          {!loading && !loadError && (
            <span className="gr-count" aria-live="polite">{filteredGroups.length} grup</span>
          )}
        </div>

        {/* Liste */}
        {loading ? (
          <SkeletonList />
        ) : loadError && groups.length === 0 ? (
          <div className="gr-state">
            <h3>Gruplar yüklenemedi</h3>
            <p>Bağlantını kontrol edip tekrar dene.</p>
            <button type="button" className="gr-btn gr-btn-solid" onClick={() => { setLoading(true); fetchGroups(); }}>
              Tekrar dene
            </button>
          </div>
        ) : filteredGroups.length > 0 ? (
          <GroupList
            groups={filteredGroups}
            user={user}
            query={searchTerm}
            handlers={{
              onDelete: handleDeleteGroup,
              onJoin: handleJoinGroup,
              onLeave: handleLeaveGroup,
              onRequest: handleRequestJoin,
              onCancelRequest: handleCancelRequest,
              onOpenCode: setCodeGroup,
              onManage: setManageGroup,
              askConfirm,
            }}
          />
        ) : hasActiveFilters ? (
          <div className="gr-state">
            <h3>Eşleşen grup yok</h3>
            <p>Arama veya filtreleri değiştirerek tekrar dene.</p>
            <button type="button" className="gr-btn gr-btn-outline" onClick={clearFilters}>Filtreleri temizle</button>
          </div>
        ) : (
          <div className="gr-state">
            <h3>Henüz grup yok</h3>
            <p>İlk çalışma grubunu oluşturup arkadaşlarını davet et.</p>
            <button type="button" className="gr-btn gr-btn-solid" onClick={() => setShowCreate(true)}>
              Yeni grup oluştur
            </button>
          </div>
        )}

        <div className="gr-back">
          <Link to="/" className="gr-back-link">← Ana sayfaya dön</Link>
        </div>
      </main>

      {/* Mobil hızlı eylem düğmesi */}
      <button
        type="button"
        className="gr-fab"
        onClick={() => setShowCreate(true)}
        aria-label="Yeni grup oluştur"
      >
        <PlusIcon className="gr-btn-icon" />
        <span>Yeni grup</span>
      </button>

      {showCreate && (
        <CreateGroupDialog onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}

      {codeGroup && (
        <JoinCodeDialog
          group={codeGroup}
          onClose={() => setCodeGroup(null)}
          onSubmit={handleJoinGroup}
        />
      )}

      {manageGroup && (
        <ManageDialog
          group={manageGroup}
          onClose={() => setManageGroup(null)}
          onChanged={fetchGroups}
          askConfirm={askConfirm}
        />
      )}

      {/* En sonda render edilir: diğer pencerelerin üstünde açılır */}
      {confirmState && (
        <ConfirmDialog
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onCancel={() => setConfirmState(null)}
          onConfirm={async () => {
            try {
              await confirmState.onConfirm();
            } finally {
              setConfirmState(null);
            }
          }}
        />
      )}
    </div>
  );
}

export default Groups;